import { createSelector } from 'reselect';

import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type {
    CalculationEndpointType,
    GenericObject,
    IPencilOfferInputs,
    Offer,
    OfferType,
    paymentServicesTypes,
    TermOverrides,
    Terms
} from '@makemydeal/dr-platform-types';
import { BALLOON, CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import { APPLY_CREDIT_DECISION_DETAILS } from '../types/actionTypes';
import * as actionTypes from '../types/actionTypes';
import type { ITerm, RequestOverrideOptions, TermIdParam } from '../types/paymentTypes';

import { CASH_DOWN_FIELD_NAME, REQUESTED_DOWN_PAYMENT_FIELD_NAME } from '../constants/meta';
import { PAYMENT_REQUEST_OVERRIDE_NODE, ValidatePreferredTerm } from '../constants/payment';
import { CALC_ENDPOINTS } from '../constants/state';
import {
    getAutoTermId,
    getCalculationEndpointByOfferType,
    getDealerDefaultTermsByOfferType,
    getTermId,
    getTermIdByOfferType,
    getTermMonths,
    hasRecordedIncentiveSelection,
    isFordFlexBuyDeal
} from '../selectors/index';
import { getErrorMessages, getTermStrategy, shouldUseGenericCalcs } from '../selectors/paymentSelectors';
import type { DownPaymentInputs, DownPaymentOptions, GetDownPaymentField } from '../types/commonTypes';
import type { OfferTermOffer, PreferredTerms } from '../types/offerTypes';
import { isExpectedDownPayment } from './common';
import { isCashDealIncentivesFixesEnabled } from './incentives';
import { parseTermId } from './termIdParser';

export const isValidPayment = function (payment: any): boolean {
    // TODO: need to validate payment object.
    const paymentMethods = [CASH, FINANCE, LEASE];
    return payment.success || paymentMethods.filter((offerType) => payment[offerType]?.success === true).length ? true : false;
};

export const NEGATIVE_AMORTIZATION_ERROR_MESSAGE = 'Negative amortization';

const isNegativeLeasePayment = (payment: GenericObject): boolean => {
    const monthlyPayment = Number(payment?.monthlyPayment);
    return (
        monthlyPayment < 0 ||
        Number(payment?.baseMonthlyPayment) < 0 ||
        (Number(payment?.diaDueAtSigning) < 0 && !(monthlyPayment > 0))
    );
};

export const getNegativeLeaseQuoteError = (result: GenericObject | undefined, offerType: OfferType): GenericObject | undefined => {
    if (offerType !== LEASE) {
        return undefined;
    }
    const payments = result?.[LEASE]?.payments;
    if (!Array.isArray(payments) || payments.length === 0) {
        return undefined;
    }
    return payments.every(isNegativeLeasePayment) ? { errors: [{ message: NEGATIVE_AMORTIZATION_ERROR_MESSAGE }] } : undefined;
};

/**
 * Which calculation engine a request was built for, read off the lane the payload builder resolved.
 *
 * Not derivable from the URL - MV posts every request to a single orchestrator endpoint and the engine is
 * selected inside the payload - nor from state at response time, since the deal can switch lanes in
 * flight. Returns undefined when the builder did not run, so callers stay closed rather than guessing.
 */
export const resolveRequestCalculationEndpointFromAction = (action: AnyFSA): string | undefined => {
    const usedGenericCalcs = action?.meta?.usedGenericCalcs;
    if (typeof usedGenericCalcs !== 'boolean') {
        return undefined;
    }
    return usedGenericCalcs ? CALC_ENDPOINTS.userProgramQuotes : CALC_ENDPOINTS.programQuotes;
};

/**
 * True when a payment originated from the payment grid's fan-out rather than a deal-screen edit. Those
 * responses must never write the frozen rebate set - they leave incentive-less term records behind.
 *
 * Shared with `extractPaymentReceivedContext`'s `isFlexiblePaymentsGrid` so the two cannot drift.
 */
export const isPaymentGridOriginatedAction = (originalAction: any): boolean =>
    [
        actionTypes.UPDATE_FINANCE_TERMS_GRID,
        actionTypes.UPDATE_LEASE_TERMS_GRID,
        actionTypes.UPDATE_BALLOON_TERMS_GRID,
        actionTypes.FETCH_TERMS_GRID_DATA
    ].includes(originalAction?.type) || originalAction?.meta?.paymentGrid?.gridAction === true;

export const withMeta = (action: AnyFSA, originalAction: AnyFSA, state?: Record<string, any>) => {
    const passthrough = originalAction?.meta?.passthrough;
    if (passthrough != null) {
        return {
            ...action,
            meta: {
                ...action.meta,
                passthrough,
                originalAction
            }
        };
    }
    return {
        ...action,
        meta: {
            ...action.meta,
            originalAction,
            offerType: state?.offer?.offerType
        }
    };
};

export type GetOfferTypeTermsTerms = Pick<Terms, 'financeTerms' | 'leaseTerms' | 'balloonTerms'>;

/**
 * US2075533: `overridesByTerm` is now populated for every priced term, because each carries the rate type its
 * column is labelled with. Presence of the map therefore no longer means the user overrode anything, so the
 * readers that treat it as user intent must ask this instead. An entry holding nothing but `rateType` is a
 * label, not an override.
 */
export const hasUserTermOverrides = (overridesByTerm?: Record<string | number, TermOverrides>): boolean =>
    Object.values(overridesByTerm ?? {}).some((termOverride) => {
        const { rateType: _label, ...userAuthored } = termOverride ?? {};
        return Object.values(userAuthored).some((value) => value != null);
    });

export function getOfferTypeTerms(offerType: OfferType, terms: GetOfferTypeTermsTerms): string[] | ITerm[] {
    switch (offerType) {
        case FINANCE: {
            return terms.financeTerms!;
        }
        case LEASE: {
            return terms.leaseTerms!;
        }
        case BALLOON: {
            return terms.balloonTerms!;
        }
        default: {
            // fallback for now just in case we don't have all terms covered by this logic?
            return (terms as any)[`${offerType}Terms`];
        }
    }
}

function isNotITerm(termData: ITerm | string): termData is string {
    return typeof termData === 'string';
}

function isFieldDefFunction(fieldDef: string | GetDownPaymentField): fieldDef is GetDownPaymentField {
    return typeof fieldDef === 'function';
}

export function getIsPreferredTermField(
    offerType: OfferType,
    terms: PreferredTerms,
    fieldDef: string | GetDownPaymentField
): number | null {
    if (terms) {
        const offerTypeTerms = getOfferTypeTerms(offerType, terms);
        for (const term in offerTypeTerms) {
            const termId = offerTypeTerms[term];
            if (isNotITerm(termId)) {
                const termData = terms.byId?.[termId] as ITerm;
                const termIsPreferred = termData.isPreferred;
                if (termIsPreferred) {
                    const propField: string = isFieldDefFunction(fieldDef) ? fieldDef(termData) : fieldDef;
                    const value = (termData as any)[propField];
                    return !isNaN(Number(value)) ? Number(value) : value;
                }
            }
        }
    }
    return null;
}

export const validateSelectedTerm = (offerType: OfferType, terms: Terms): TermIdParam => {
    const validatedParams: TermIdParam = {};

    Object.keys(ValidatePreferredTerm).forEach((key) => {
        const getDownPaymentItem = (term: any) => getDownPaymentItemForOfferType(offerType, term);
        const fieldDef = key === 'DOWNPAYMENT' ? getDownPaymentItem : (ValidatePreferredTerm as any)[key];

        validatedParams[key] = getIsPreferredTermField(offerType, terms, fieldDef)!;
    });

    return validatedParams;
};

export const isValidTermId = (offerType: OfferType, termId: string | undefined, payload: any, offerState?: Offer): boolean => {
    const hasTermId = payload?.terms?.[`${offerType}Terms`]?.length > 0;
    const hasTerm = payload.terms[`${offerType}Terms`]?.indexOf(termId) > -1;

    if (!hasTermId || !hasTerm) {
        return false;
    }

    if (offerType === FINANCE) {
        if (offerState && isFordFlexBuyDeal(offerState) && !payload.flexBuyPayment) {
            return false;
        }
        if (offerState && !isFordFlexBuyDeal(offerState) && payload.flexBuyPayment) {
            return false;
        }
    }

    return true;
};

export const validateSelectedTermWithIsPreferred = (
    offerType: OfferType,
    termId: string | undefined,
    payload: OfferTermOffer,
    offerState?: Offer
): string | null | undefined => {
    const isValidId = isValidTermId(offerType, termId, payload, offerState);

    if (isValidId) {
        return termId;
    }

    if (offerState) {
        const validatedParams: TermIdParam = validateSelectedTerm(offerType, payload.terms);

        if (!(validatedParams.TERM > 0)) return null;
        return (
            getAutoTermId(offerState, validatedParams.TERM, {
                offerType,
                extension: validatedParams
            }) || null
        );
    } else {
        const getDownPaymentItem = (term: any) => getDownPaymentItemForOfferType(offerType, term);
        const termMonths = getIsPreferredTermField(offerType, payload.terms, 'months');
        const downPayment = getIsPreferredTermField(offerType, payload.terms, getDownPaymentItem);

        if (termMonths == null || !(termMonths > 0)) return null;
        return getTermId(offerType, termMonths, downPayment as number) || null;
    }
};

export const parseSelectedTermMonths = (termId: string): number | undefined => {
    if (!termId) {
        return undefined;
    }

    const idParams = parseTermId(termId);

    if (!idParams || Object.keys(idParams).length === 0) {
        return undefined;
    }

    return idParams.termIdParams.term;
};

export const getTermsListByStrategy = (
    state: Record<string, any>,
    offerType: OfferType,
    termMonths?: number
): number[] | undefined => {
    if (offerType === CASH) {
        return undefined;
    }

    const termStrategy = getTermStrategy(state);
    const termId = getTermIdByOfferType(state, offerType);
    const currentTerm = parseSelectedTermMonths(termId) as number;
    const dealerTerms = getDealerDefaultTermsByOfferType(state, offerType);
    const allTerms = getTermMonths(dealerTerms);

    switch (termStrategy) {
        case 'all':
            return allTerms;
        case 'current':
        default:
            return termMonths ? [termMonths] : [currentTerm];
    }
};

export const canOverrideResidualValue = (originalPayload: GenericObject): boolean => {
    return originalPayload == null || !Object.keys(originalPayload).includes('value') || originalPayload?.value != null;
};

export const isBaseResidualAction = (originalActionType: string): boolean => {
    return Boolean(originalActionType.match(/_BASE_/));
};

export const isProgramResidualAction = (originalActionType: string): boolean => {
    return Boolean(originalActionType.match(/_PROGRAM_/));
};

export const isAdjustedResidualAction = (originalActionType: string): boolean => {
    return Boolean(originalActionType.match(/_ADJUSTED_/));
};

export const isBalloonPaymentAction = (originalActionType: string): boolean => {
    return Boolean(originalActionType.match(/_BALLOON_/));
};
export const shouldUseGenericCalcsStructure = (
    state: OfferTermOffer,
    paymentOfferType: OfferType,
    hostSelectors: GenericObject,
    callOptions?: RequestOverrideOptions
): boolean => {
    const { isRollToCall, isProgramCall, enableOverridesForSalesView, nonSelectedTermHonoringSV } = callOptions || {};

    if (isProgramCall) {
        return false;
    }
    if (isRollToCall) {
        return true;
    }

    if (nonSelectedTermHonoringSV && paymentOfferType === LEASE) {
        return false;
    }

    // UserProgramQuotes (the "generic calcs" shape) has no way to carry catalog incentive ids for
    // cash - only ProgramQuotes does. Once a cash incentive selection is recorded, every subsequent
    // recalc must stay on ProgramQuotes regardless of the calc-endpoint stash/restore choreography
    // (e.g. closing the Programs slideout reverting cash back to UserProgramQuotes), or the selection
    // silently drops off the deal. See DE759607/US2076398.
    if (paymentOfferType === CASH && isCashDealIncentivesFixesEnabled(state) && hasRecordedIncentiveSelection(state, CASH)) {
        return false;
    }

    if (enableOverridesForSalesView && paymentOfferType !== CASH) {
        return getCalculationEndpointByOfferType(state, paymentOfferType) === CALC_ENDPOINTS.userProgramQuotes;
    }

    return shouldUseGenericCalcs(state, hostSelectors, paymentOfferType);
};

export const isApplyingCreditDecision = (action: AnyFSA) => {
    return action?.meta?.[PAYMENT_REQUEST_OVERRIDE_NODE] && action?.type === APPLY_CREDIT_DECISION_DETAILS;
};

export const upqResidualInfoOverrides = (action: GenericObject, termId: string | undefined): IPencilOfferInputs => {
    const payload = action.payload;
    const originalAction = action.meta?.originalAction || {};
    const originalActionType = originalAction.type;

    const originalPayload = originalAction.payload;
    const canOverride = canOverrideResidualValue(originalPayload);
    const isBaseAction = isBaseResidualAction(originalActionType);
    const isProgramAction = isProgramResidualAction(originalActionType);
    const isAdjustedAction = isAdjustedResidualAction(originalActionType);
    const isBalloonAction = isBalloonPaymentAction(originalActionType);

    const newOfferInputs: IPencilOfferInputs = {};

    const termData = payload.terms.byId[termId!];

    if ((canOverride && isBaseAction) || isAdjustedAction) {
        newOfferInputs.baseResidualAmountOverride = termData?.baseResidualDollar;
        newOfferInputs.baseResidualPercentOverride = termData?.baseResidualPercent;
        newOfferInputs.programResidualOverride = termData?.programResidual;
        newOfferInputs.programResidualPercentOverride = termData?.programResidualPercent;
    }

    if (canOverride && isProgramAction) {
        newOfferInputs.programResidualOverride = termData?.programResidual;
        newOfferInputs.programResidualPercentOverride = termData?.programResidualPercent;
    }

    if ((isAdjustedAction && canOverride) || isBaseAction) {
        newOfferInputs.adjustedResidualAmountOverride = termData?.adjustedResidualValue;
        newOfferInputs.adjustedResidualPercentOverride = termData?.adjustedResidualPercent;
    }

    if (isBalloonAction && canOverride) {
        newOfferInputs.balloonPaymentAmountOverride = termData?.baseResidualDollar;
        newOfferInputs.balloonTypeOverride = termData?.balloonType;
        newOfferInputs.baseResidualAmountOverride = termData?.baseResidualDollar;
        newOfferInputs.programResidualOverride = termData?.programResidual;
        newOfferInputs.adjustedResidualAmountOverride = termData?.adjustedResidualValue;
    }

    return newOfferInputs;
};

export const pqResidualInfoOverrides = (state: GenericObject, action: GenericObject, offerType: OfferType): IPencilOfferInputs => {
    const payload = action.payload;
    const originalAction = action.meta?.originalAction || {};
    const originalActionType = originalAction.type;

    //TODO: logical error here, need to correct
    // if action.meta.originalAction is undefined then originalActionType will be undefined
    // and the below functions will not work as expected
    const originalPayload = originalAction.payload;
    const canOverride = canOverrideResidualValue(originalPayload);
    const isBaseAction = isBaseResidualAction(originalActionType);
    const isProgramAction = isProgramResidualAction(originalActionType);
    const isAdjustedAction = isAdjustedResidualAction(originalActionType);

    const newOfferInputs: IPencilOfferInputs = {};

    // check all terms; set only the overridden ones
    const terms = payload.terms;
    const residualInfoByTermOverride: Record<any, any> = {};
    for (const leaseTermId of terms.leaseTerms) {
        const leaseTerm = terms.byId[leaseTermId];
        const {
            months,
            adjustedResidualPercent,
            adjustedResidualValue,
            baseResidualPercent,
            baseResidualDollar,
            programResidual,
            programResidualPercent
        } = leaseTerm;
        const overrideForTerm = state?.byOfferType?.[LEASE]?.overridesByTerm?.[leaseTerm.months]?.residualInfo;
        if (overrideForTerm != null) {
            // NOTE: if the override exists at all; set it.
            residualInfoByTermOverride[months] = {};

            if ((canOverride && isBaseAction) || isAdjustedAction) {
                if (baseResidualPercent) {
                    residualInfoByTermOverride[months].baseResidualPercent = baseResidualPercent;
                }
                if (baseResidualDollar) {
                    // NOTE: PS responds with this inconsistent prop name. *Dollar vs *Amount
                    residualInfoByTermOverride[months].baseResidualAmount = baseResidualDollar;
                }
                if (programResidual) {
                    residualInfoByTermOverride[months].programResidual = programResidual;
                }
                if (programResidualPercent) {
                    residualInfoByTermOverride[months].programResidualPercent = programResidualPercent;
                }
            }

            if (canOverride && isProgramAction) {
                if (programResidual != null) {
                    residualInfoByTermOverride[months].programResidual = programResidual;
                }
                if (programResidualPercent != null) {
                    residualInfoByTermOverride[months].programResidualPercent = programResidualPercent;
                }
            }

            if ((canOverride && isAdjustedAction) || isBaseAction) {
                if (adjustedResidualPercent) {
                    residualInfoByTermOverride[months].adjustedResidualPercent = adjustedResidualPercent;
                }
                if (adjustedResidualValue) {
                    residualInfoByTermOverride[months].adjustedResidualAmount = adjustedResidualValue;
                }
            }
        }
    }

    // NOTE: If any were overridden, update penciled inputs prop
    if (Object.keys(residualInfoByTermOverride).length) {
        const prevOverridesByTerm = state.byOfferType[offerType]?.overridesByTerm;
        const newOverridesByTerm: Record<string, any> = {};
        for (const term in residualInfoByTermOverride) {
            newOverridesByTerm[term] = {
                ...prevOverridesByTerm[term],
                residualInfo: {
                    ...prevOverridesByTerm[term].residualInfo,
                    ...residualInfoByTermOverride[term]
                }
            };
        }
        newOfferInputs.overridesByTerm = newOverridesByTerm;
    }

    return newOfferInputs;
};

export const getPaymentErrorMessages = createSelector(getErrorMessages, (errors: GenericObject): paymentServicesTypes.Message[] => {
    const createItem = (message: string, type: string): paymentServicesTypes.Message => {
        return {
            message: message,
            messageType: type
        };
    };

    const messages = errors.map((error: any) => {
        return typeof error?.originalPSError?.validationErrors === 'object'
            ? Object.keys(error.originalPSError.validationErrors)
                  .map((key) => {
                      return Array.isArray(error.originalPSError.validationErrors[key])
                          ? error.originalPSError.validationErrors[key]
                                .map((item) => {
                                    return createItem(item, key);
                                })
                                .flat()
                          : createItem(error.originalPSError.validationErrors[key], key);
                  })
                  .flat()
            : createItem(error.message, error.serviceName);
    });

    return messages.flat();
});

export const getDownPaymentItemForOfferType = (
    offerType: OfferType,
    term: ITerm,
    fieldName: DownPaymentInputs = REQUESTED_DOWN_PAYMENT_FIELD_NAME,
    options: DownPaymentOptions = {}
): DownPaymentInputs => {
    const { useResolutionLogic = true } = options;

    if (!useResolutionLogic) {
        return fieldName;
    }

    return offerType === LEASE && isExpectedDownPayment(term[fieldName] as number, term.minimumCashDown)
        ? CASH_DOWN_FIELD_NAME
        : fieldName;
};

export const getDownPaymentForOfferType = (offerType: OfferType, term: ITerm, options: DownPaymentOptions = {}): number => {
    const propName = getDownPaymentItemForOfferType(offerType, term, REQUESTED_DOWN_PAYMENT_FIELD_NAME, options);
    return term[propName] as number;
};

export function isCalculationEndpoint(value: any): value is CalculationEndpointType {
    return value === CALC_ENDPOINTS.programQuotes || value === CALC_ENDPOINTS.userProgramQuotes;
}

export const createEndpointsForAllTypes = (endpoint: CalculationEndpointType) => ({
    [FINANCE]: endpoint,
    [LEASE]: endpoint,
    [CASH]: endpoint,
    [BALLOON]: endpoint
});
